import { nayttonimi, selite } from "./nayttonimi";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useKohde } from "./Kohdekonteksti";
import { Estelaatikko } from "./Este";
import { Tilasana } from "./Tila";
import { naytaPvm } from "./pvm";
import { nimeamattomanSana } from "./sanat";
import { luku } from "./luvut";
import {
  api, ApiError, maanantai, pvmTeksti, lisaaPaivia, perjantai,
  varaaKapasiteettia,
  type Kayttaja, type Kohde, type Resurssi, type AllokaatioRivi, type Kuukausikuorma,
  type Poissaolo, type Poissaololaji, type HenkilonRivi,
} from "./api";
import type {
  KapasiteettiPaiva, KapasiteettiViikko, KapasiteettiRekisteri, Vaje, Varamiehet,
} from "./shared/types";

import { Ohje } from "./Ohje";
import { t, virheTeksti, lokaali } from "./kieli";
/* ═══════════════════════════════════════════════════════════
   Kapasiteettinäkymä.
   Lukee f_kapasiteetti_viikko, f_vaje ja f_varamiehet -funktiot
   API:n läpi. Solun avaus näyttää mihin kohteisiin viikko
   jakautuu, ja sallii allokaation muuttamisen.
   ═══════════════════════════════════════════════════════════ */

/** Kahdeksan viikkoa päivinä olisi 40 saraketta, joten päivätilassa
 *  ikkuna kapenee. Viikonloput jätetään pois kokonaan. */
const VIIKKOJA = { viikko: 8, paiva: 3 } as const;

type Tarkkuus = keyof typeof VIIKKOJA;

export default function Kapasiteetti({ kayttaja }: { kayttaja: Kayttaja }) {
  const saaMuokata = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));

  const [tarkkuus, setTarkkuus] = useState<Tarkkuus>("viikko");
  const [alku, setAlku] = useState(() => pvmTeksti(maanantai(new Date())));
  const loppu = useMemo(
    () => pvmTeksti(lisaaPaivia(new Date(alku), VIIKKOJA[tarkkuus] * 7 - 1)),
    [alku, tarkkuus]
  );

  const [rivit, setRivit] = useState<KapasiteettiViikko[]>([]);
  const [paivat, setPaivat] = useState<KapasiteettiPaiva[]>([]);
  const [vaje, setVaje] = useState<Vaje[]>([]);
  const [vara, setVara] = useState<Varamiehet[]>([]);
  const [kuukaudet, setKuukaudet] = useState<Kuukausikuorma[]>([]);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [resurssit, setResurssit] = useState<Resurssi[]>([]);
  const [poissaololajit, setPoissaololajit] = useState<Poissaololaji[]>([]);
  const [rekisteri, setRekisteri] = useState<KapasiteettiRekisteri | null>(null);
  // 13.9.2026: vaje, varamiehet ja kuukausikuorma ovat koko yrityksen tietoa eivätkä näy rajatulle (API 403).
  const [yritystasoKiinni, setYritystasoKiinni] = useState(false);
  const [ladataan, setLadataan] = useState(true);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [valittu, setValittu] = useState<
    { id: number; viikkoAlku: string; alkaa: string; paattyy: string } | null>(null);
  const [suodin, setSuodin] = useState("Kaikki");
  const [henkilo, setHenkilo] = useState<KapasiteettiViikko | null>(null);

  const lataa = useCallback(async () => {
    setLadataan(true);
    setVirhe(null);
    try {
      // Rajatulle nämä kolme vastaavat 403 (päätös 13.9.2026). Vain se kielto luetaan tyhjäksi — muu
      // virhe kaataa latauksen kuten ennenkin, eikä kiellettyä näytetä nollana (ks. huomautus alla).
      let kiinni = false;
      const yritystaso = <T,>(p: Promise<T[]>): Promise<T[]> => p.catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 403) { kiinni = true; return [] as T[]; }
        throw e;
      });
      const [k, v, w, kk, ko, re, pv, pl, rek] = await Promise.all([
        api.kapasiteetti(alku, loppu),
        yritystaso(api.vaje(alku, loppu)),
        yritystaso(api.varamiehet(alku, loppu)),
        yritystaso(api.kuukausikuorma(alku, loppu)),
        api.kohteet(),
        api.resurssit(),
        tarkkuus === "paiva" ? api.kapasiteettiPaiva(alku, loppu) : Promise.resolve([]),
        api.poissaololajit(),
        api.kapasiteettiRekisteri(alku, loppu),
      ]);
      setRivit(k); setVaje(v); setVara(w); setKuukaudet(kk);
      setKohteet(ko); setResurssit(re); setPaivat(pv); setPoissaololajit(pl);
      setRekisteri(rek);
      setYritystasoKiinni(kiinni);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kap.haku_virhe"));
    } finally {
      setLadataan(false);
    }
  }, [alku, loppu, tarkkuus]);

  useEffect(() => { void lataa(); }, [lataa]);

  /* ── johdetut ────────────────────────────────────────── */

  const viikot = useMemo(() => {
    const nahdyt = new Map<string, number>();
    for (const r of rivit) nahdyt.set(r.viikko_alku, r.viikko);
    return [...nahdyt.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rivit]);

  /**
   * Päivätilassa näkyvät kaikki seitsemän päivää.
   *
   * Ehdollinen sääntö — "näytä vain työpäivät ja allokoidut" — vaikutti
   * siistiltä mutta esti sunnuntaityön luomisen kokonaan: ruutua ei
   * ollut olemassa, joten sitä ei voinut klikata. Ja sarakkeiden
   * ilmestyminen ja katoaminen datan mukaan tekee ruudukosta
   * arvaamattoman.
   *
   * Viikonloput erottuvat väreillä, joten ne eivät sekoitu arkeen.
   */
  const tyopaivat = useMemo(
    () => [...new Set(paivat.map((p) => p.pvm))].sort(),
    [paivat]
  );

  const paivaSolu = useCallback(
    (id: number, pvm: string) => paivat.find((p) => p.henkilo_id === id && p.pvm === pvm),
    [paivat]
  );

  const luokat = useMemo(
    () => [...new Set(vara.map((v) => v.osaamisluokka))].sort(),
    [vara]
  );

  const henkilot = useMemo(() => {
    const m = new Map<number, KapasiteettiViikko>();
    for (const r of rivit) if (!m.has(r.henkilo_id)) m.set(r.henkilo_id, r);
    const kaikki = [...m.values()];
    if (suodin === "Kaikki") return kaikki;
    const idt = new Set(
      resurssit.filter((r) => r.osaamiset.includes(suodin)).map((r) => r.id)
    );
    return kaikki.filter((h) => idt.has(h.henkilo_id));
  }, [rivit, suodin, resurssit]);

  const solu = useCallback(
    (id: number, vk: string) => rivit.find((r) => r.henkilo_id === id && r.viikko_alku === vk),
    [rivit]
  );

  const harkko = useMemo(() =>
    viikot.map(([vkAlku, vkNro]) => {
      const r = rivit.filter((x) => x.viikko_alku === vkAlku);
      const kap = r.reduce((s, x) => s + x.maksimi, 0);
      const all = r.reduce((s, x) => s + x.allokoitu, 0);
      const v = vaje.filter((x) => x.viikko_alku === vkAlku);
      return {
        vkAlku, vkNro,
        osuus: kap > 0 ? all / kap : 0,
        alihankinta: v.reduce((s, x) => s + x.alihankintatarve, 0),
        vaje: v.reduce((s, x) => s + x.vaje, 0),
      };
    }), [viikot, rivit, vaje]);

  const paatokset = useMemo(() => {
    const lista: { taso: string; otsikko: string; teksti: string }[] = [];
    for (const v of vaje) {
      if (v.vaje > 0) lista.push({
        taso: "vaje",
        otsikko: t("kap.pk_vaje_otsikko", { luokka: v.osaamisluokka, vk: v.viikko }),
        teksti: t("kap.pk_vaje_teksti", { h: luku(v.vaje) }),
      });
      else if (v.alihankintatarve > 0) lista.push({
        taso: "alihankinta",
        otsikko: t("kap.pk_alih_otsikko", { luokka: v.osaamisluokka, vk: v.viikko }),
        teksti: t("kap.pk_alih_teksti", { h: luku(v.alihankintatarve) }),
      });
    }
    for (const r of rivit) {
      if (r.virhe_poissa) lista.push({
        taso: "ristiriita",
        otsikko: t("kap.pk_ristiriita_otsikko", { nimi: r.nimi }),
        teksti: t("kap.pk_ristiriita_teksti", { vk: r.viikko, h: luku(r.allokoitu) }),
      });
      else if (r.ylitys) {
        // Viikkorivin `ylitys` on TAI kahdesta: viikkosumma yli
        // viikkomaksimin, TAI jokin yksittainen paiva yli paivamaksimin.
        // Teksti sanoi aina viikkoluvut, joten paivaylityksen kohdalla
        // punaisen vieressa luki "65 h, maksimi 68 h" -- vari ja selitys
        // eri mielta. Mitattu protot 31.8.2026: viikko 65 < 68, mutta
        // ma-pe 13 h/pv > 12. Ristiriita ruudulla on pahempi kuin
        // vaara luku, koska se opettaa olemaan uskomatta molempia.
        const yliPaivat = paivat.filter(
          (p) => p.henkilo_id === r.henkilo_id && p.ylitys &&
                 p.pvm >= r.viikko_alku && p.pvm < viikonLoppu(r.viikko_alku)
        );
        const viikkoYlittaa = r.allokoitu > r.maksimi;
        lista.push({
          taso: "ylitys",
          otsikko: viikkoYlittaa
            ? t("kap.pk_yli_viikko", { nimi: r.nimi, vk: r.viikko })
            : t("kap.pk_yli_paiva", { nimi: r.nimi, vk: r.viikko }),
          teksti: viikkoYlittaa
            ? t("kap.pk_yli_viikko_teksti", { h: luku(r.allokoitu), max: luku(r.maksimi) })
            : t("kap.pk_yli_paiva_alku", { h: luku(r.allokoitu), max: luku(r.maksimi) }) + " " +
              (yliPaivat.length
                ? t("kap.pk_yli_paiva_lkm", {
                    n: yliPaivat.length,
                    a: luku(Math.max(...yliPaivat.map((p) => p.vahvistettu + p.ehdotus))),
                    b: luku(yliPaivat[0]!.maksimi) })
                : t("kap.pk_yli_paiva_jokin")),
        });
      }
    }
    // Kuukausiraja ei näy ruudukossa, koska viikko voi kulkea kahden
    // kuukauden yli. Siksi se ilmoitetaan vain tässä.
    for (const kk of kuukaudet) {
      if (!kk.ylitys) continue;
      lista.push({
        taso: "ylitys",
        otsikko: t("kap.pk_kk_otsikko", { nimi: kk.nimi, kk: kuukaudenNimi(kk.kuukausi) }),
        teksti: t("kap.pk_kk_teksti", { h: luku(kk.allokoitu), max: luku(kk.maksimi) }),
      });
    }
    const jarjestys: Record<string, number> = { vaje: 0, ristiriita: 1, ylitys: 2, alihankinta: 3 };
    return lista.sort((a, b) => (jarjestys[a.taso] ?? 9) - (jarjestys[b.taso] ?? 9));
  }, [vaje, rivit, kuukaudet, paivat]);

  /* ── näkymä ──────────────────────────────────────────── */

  if (virhe) {
    return (
      <div className="tyhja">
        <p>{virhe}</p>
        <button className="nappi nappi--paa" onClick={() => void lataa()}>{t("kap.yrita_uudelleen")}</button>
      </div>
    );
  }

  if (!ladataan && rivit.length === 0) {
    return (
      <div className="tyhja">
        <h2>{t("kap.ei_henkiloita")}</h2>
        <p>{t("kap.ei_henkiloita_teksti")}</p>
      </div>
    );
  }

  return (
    <>
      <header className="head">
        <div className="eyebrow">
          {t("kap.eyebrow")}
          {harkko.length > 0 && ` · ${t("kap.viikot_vali", { a: harkko[0]!.vkNro, b: harkko[harkko.length - 1]!.vkNro })}`}
        </div>
        <div className="head__rivi">
          <h1>{t("kap.otsikko")}</h1>
          <div className="head__ohjaus">
            <button className="nappi nappi--kevyt" onClick={() => siirra(-7)}>← {t("kap.edellinen")}</button>
            <button className="nappi nappi--kevyt" onClick={() => setAlku(pvmTeksti(maanantai(new Date())))}>{t("kap.tanaan")}</button>
            <button className="nappi nappi--kevyt" onClick={() => siirra(7)}>{t("kap.seuraava")} →</button>
          </div>
        </div>
      </header>

      {/* Ohje ylös (Toni 23.9.2026): kuka saa tehdä mitä, ja mitä luvut tarkoittavat. */}
      <Ohje
        kuka={<>{t("kap.ohje.kuka")}</>}
        luvut={<>{t("kap.ohje.luvut")}</>} />

      {ladataan && <div className="lataa lataa--ohut">{t("kap.paivitetaan")}</div>}

      {/* Tyhjä vaje näyttäisi siltä että väkeä riittää — kielletty ei ole nolla. */}
      {yritystasoKiinni && (
        <p className="huom" data-yritystaso="kiinni">
          {t("kap.yritystaso_kiinni")}
        </p>
      )}

      {/* Rekisterin tila. EI korjaus vaan mittaus: paatti-kenttä on
          olemassa mutta se on NULL kaikilla, kaikissa instansseissa.
          Mitattu tyhjä on eri asia kuin näkymätön tyhjä — ja tämä rivi
          kertoo lukijalle sen mitä hän jo tietää työmaalta, mikä on se
          hetki jossa lukuun alkaa uskoa. */}
      {rekisteri && (
        <p className="rekisteririvi">
          <strong>{rekisteri.henkiloita}</strong> {t("kap.henkiloa_laskennassa")}
          {" · "}
          <strong>{rekisteri.paattyneita}</strong> {t("kap.merkitty_paattyneeksi")}
          {rekisteri.paattyneita === 0 && rekisteri.henkiloita > 0 && (
            <>
              <br />
              {t("kap.joku_lahtenyt")}
            </>
          )}
          {" · "}
          <strong>{rekisteri.tyoaikamalli}</strong>/{rekisteri.henkiloita} {t("kap.tyoaikamallilla")}
        </p>
      )}

      {/* viikkoharkko */}
      <section className="harkko" aria-label={t("kap.harkko_aria")}>
        {harkko.map((h) => (
          <div key={h.vkAlku}
               className={"vk" + (h.vaje > 0 ? " vk--vaje" : h.alihankinta > 0 ? " vk--alih" : "")}>
            <div className="vk__num">{h.vkNro}</div>
            <div className="vk__mittari">
              <span className="vk__taytto" style={{ height: `${Math.min(h.osuus, 1) * 100}%` }} />
              {h.osuus > 1 && <span className="vk__yli" />}
            </div>
            <div className="vk__alle">
              {h.vaje > 0 ? t("kap.vk_vaje", { h: luku(h.vaje) })
                : h.alihankinta > 0 ? t("kap.vk_vuokra", { h: luku(h.alihankinta) })
                : `${Math.round(h.osuus * 100)} %`}
            </div>
          </div>
        ))}
      </section>

      {/* Päätöslista viikkokorttien alle (Toni 18.9.2026): näkymän otsikko lupaa
          kaksi asiaa — kuka on missä ja missä se pettää. Kortit vastaavat
          ensimmäiseen, tämä toiseen, eikä toinen vastaus kuulu alaviitteiden alle.
          Mitattu ennen siirtoa: lista lasketaan `[vaje, rivit, kuukaudet, paivat]`
          -riippuvuuksista eikä `suodin`ista, joten se ei seuraa työlajisuodatinta —
          siksi se saa olla suodattimien yläpuolella. */}
      {/* 25.9.2026: tyhjänä lohko sanoo sen eikä katoa — katoava lohko ja «ei mitään huomioitavaa»
          näyttivät samalta. */}
      {!ladataan && paatokset.length === 0 && (
        <p className="dim" data-toimenpide="huomioitavaa" data-tyhja-tila="">
          {t("kap.ei_huomioitavaa")}
        </p>
      )}
      {paatokset.length > 0 && (
        <section className="paatokset" data-toimenpide="huomioitavaa">
          <div className="panel__head">
            <h2>{t("kap.huomioitavaa")}</h2>
            <span className="hint">{t("kap.kohtaa", { n: paatokset.length })}</span>
          </div>
          <ul>
            {paatokset.map((p, i) => (
              <li key={i} className={`pk pk--${p.taso}`}>
                <span className="pk__merkki" aria-hidden="true" />
                <div>
                  <div className="pk__otsikko">{p.otsikko}</div>
                  <div className="pk__teksti">{p.teksti}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* puskurimittari */}
      {luokat.length > 0 && (
        <section className="panel">
          <div className="panel__head">
            <h2>{t("kap.puskuri_otsikko")}</h2>
            <span className="hint">{t("kap.puskuri_hint")}</span>
          </div>
          <div className="scroll">
            <table className="tbl tbl--puskuri">
              <thead>
                <tr>
                  <th className="th--nimi">{t("kap.osaaminen")}</th>
                  {viikot.map(([vkAlku, vkNro]) => <th key={vkAlku}>{t("kap.vk", { n: vkNro })}</th>)}
                </tr>
              </thead>
              <tbody>
                {luokat.map((lk) => (
                  <tr key={lk}>
                    <th className="th--nimi">{lk}</th>
                    {viikot.map(([vkAlku]) => {
                      const v = vara.find((x) => x.osaamisluokka === lk && x.viikko_alku === vkAlku);
                      if (!v) return <td key={vkAlku} />;
                      return (
                        <td key={vkAlku} className={v.varamiehia === 0 ? "td--nolla" : ""}>
                          <span className="pips" title={v.riski}>
                            {Array.from({ length: Math.min(Math.max(v.luokan_koko, 3), 5) }).map((_, i) => (
                              <span key={i} className={"pip" + (i < v.varamiehia ? " pip--on" : v.varamiehia === 0 ? " pip--nolla" : "")} />
                            ))}
                          </span>
                          {v.vuokravaraa > 0 && (
                            <span className="vuokrapip" title={t("kap.vuokrapaikkaa_vapaana", { n: v.vuokravaraa })}>
                              +{v.vuokravaraa}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* suodin ja tarkkuus */}
      <div className="suodin">
        {["Kaikki", ...luokat].map((s) => (
          <button key={s} className={"chip" + (suodin === s ? " chip--on" : "")}
                  onClick={() => setSuodin(s)}>{s === "Kaikki" ? t("kap.kaikki") : s}</button>
        ))}
        <span className="suodin__erotin" />
        {(["viikko", "paiva"] as Tarkkuus[]).map((tr) => (
          <button key={tr} className={"chip" + (tarkkuus === tr ? " chip--on" : "")}
                  onClick={() => { setTarkkuus(tr); setValittu(null); }}>
            {tr === "viikko" ? t("kap.viikot") : t("kap.paivat")}
          </button>
        ))}
      </div>

      {/* ruudukko */}
      {/* T677 A / T532 (Toni 27.10.2026 klo 10.10): ruudukko vierii omassa ikkunassaan — vaakapalkki näkyvissä eikä leveä taulukko
          venytä puhelimen sivua. */}
      <section className="panel taulu-ikkuna" data-taulu-ikkuna="resurssit">
        <table className={"tbl tbl--ruudukko" + (tarkkuus === "paiva" ? " tbl--paivat" : "")}>
          <thead>
            {tarkkuus === "paiva" && (
              <tr className="tr--viikkorivi">
                <th className="th--nimi" />
                {viikot.map(([vkAlku, vkNro]) => {
                  const maara = tyopaivat.filter((p) => pvmTeksti(maanantai(new Date(p))) === vkAlku).length;
                  return maara > 0
                    ? <th key={vkAlku} colSpan={maara} className="th--viikko">{t("kap.vk", { n: vkNro })}</th>
                    : null;
                })}
              </tr>
            )}
            <tr>
              <th className="th--nimi">{t("tk.henkilo")}</th>
              {tarkkuus === "viikko"
                ? viikot.map(([vkAlku, vkNro]) => <th key={vkAlku} data-viikko-alku={vkAlku}>{t("kap.vk", { n: vkNro })}</th>)
                : tyopaivat.map((p) => (
                    <th key={p} className={"th--paiva" + viikonloppuLuokka(p)}>
                      {paivanNimi(p)}
                    </th>
                  ))}
            </tr>
          </thead>
          <tbody>
            {henkilot.map((h) => (
              <tr key={h.henkilo_id} className={h.nimeamaton ? "tr--vuokra" : ""}>
                <th className="th--nimi">
                  <button className="nimilinkki" onClick={() => setHenkilo(h)}
                          title={t("kap.nayta_aikataulu")}>
                    <span className="lyh">{h.lyhenne ?? "—"}</span>
                    <span className="kokonimi">
                      {h.nimeamaton ? nimeamattomanSana(h.tyosuhde) : h.nimi}
                    </span>
                  </button>
                </th>

                {tarkkuus === "viikko" && viikot.map(([vkAlku]) => {
                  const r = solu(h.henkilo_id, vkAlku);
                  const on = valittu?.id === h.henkilo_id && valittu.viikkoAlku === vkAlku;
                  return (
                    <td key={vkAlku} className="td--solu">
                      <button
                        className={soluLuokka(r, h.nimeamaton) + (on ? " solu--valittu" : "")}
                        onClick={() => setValittu(on ? null : {
                          id: h.henkilo_id, viikkoAlku: vkAlku,
                          alkaa: vkAlku, paattyy: perjantai(vkAlku),
                        })}
                        aria-label={`${h.nimi}, ${r ? t("kap.tuntia", { n: luku(r.allokoitu) }) : t("kap.ei_tietoa")}`}
                      >
                        {r && r.allokoitu > 0 ? luku(r.allokoitu) : ""}
                      </button>
                    </td>
                  );
                })}

                {tarkkuus === "paiva" && tyopaivat.map((pvm) => {
                  const p = paivaSolu(h.henkilo_id, pvm);
                  const on = valittu?.id === h.henkilo_id && valittu.alkaa === pvm && valittu.paattyy === pvm;
                  const tunnit = p ? p.vahvistettu + p.ehdotus : 0;
                  return (
                    <td key={pvm} className={"td--solu" + viikonloppuLuokka(pvm)}>
                      <button
                        className={paivaLuokka(p, h.nimeamaton) + (on ? " solu--valittu" : "")}
                        onClick={() => setValittu(on ? null : {
                          id: h.henkilo_id,
                          viikkoAlku: pvmTeksti(maanantai(new Date(pvm))),
                          alkaa: pvm, paattyy: pvm,
                        })}
                        aria-label={`${h.nimi}, ${pvm}, ${t("kap.tuntia", { n: luku(tunnit) })}`}
                      >
                        {tunnit > 0 ? luku(tunnit) : ""}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Selite.

            Ruudukossa oli 29.8.2026 kahdeksan visuaalista tilaa eika
            yhtaan selitetta. Se on sama mekanismi jota naytamme
            asiakkaalle vikana: merkitys tallennettu variin ilman etta
            mikaan kertoo mita vari tarkoittaa. Meilla tieto ei katoa —
            se on kannassa — mutta katsojalle se on yhta lukukelvoton.

            Lista on tassa eika CSS:ssa, ja se seuraa soluLuokka()/
            paivaLuokka():n haaroja jarjestyksessa. Uusi vari joka
            lisataan ilman selitetta jaa tasta puuttumaan nakyvasti. */}
        <ul className="selite">
          {[
            ["solu--kevyt",      t("kap.selite_kevyt")],
            ["solu--osa",        "50–95 %"],
            ["solu--taysi",      "95–100 %"],
            ["solu--yli",        t("kap.selite_yli")],
            ["solu--vapaa",      t("kap.selite_vapaa")],
            ["solu--vuokravapaa",t("kap.selite_vuokravapaa")],
            ["solu--poissa",     t("kap.selite_poissa")],
            ...(tarkkuus === "paiva"
              ? [["solu--poikkeus", t("kap.selite_poikkeus")] as const]
              : []),
            ["solu--ristiriita", t("kap.selite_ristiriita")],
          ].map(([lk, teksti]) => (
            <li key={lk}>
              <span className={"solu " + lk} aria-hidden="true" />
              {teksti}
            </li>
          ))}
        </ul>

        {/* Punaisen raja on henkilon oma, ei vakio — ja siksi se
            sanotaan tassa. Mitattu protot 29.8.2026: 29 henkiloa
            31:sta ajaa migraation 001 koskemattomalla oletuksella
            10 h/pv x 5 pv = 50 h/vko. Kenttaviikko on 68 h, joten
            jokainen oikein kirjattu kenttaviikko olisi punainen. */}
        {rekisteri && rekisteri.raja_min != null && rekisteri.raja_max != null && (
          <p className="selite__raja">
            {t("kap.punainen")}
            {rekisteri.raja_min === rekisteri.raja_max
              ? <> ({t("kap.hvko_kaikilla", { h: luku(rekisteri.raja_min) })})</>
              : <> ({luku(rekisteri.raja_min)}–{luku(rekisteri.raja_max)} {t("kap.hvko")})</>}
            {rekisteri.oletusmaksimi > 0 && (
              <>
                {" · "}
                <strong>{rekisteri.oletusmaksimi}</strong>/{rekisteri.henkiloita}{" "}
                {t("kap.oletusmaksimi")}
              </>
            )}
          </p>
        )}

        {/* Tiedetty ylakantti sanotaan aaneen.

            Tasainen tuntimalli ei osaa "12 h ma-pe + 8 h la": lauantai saa
            saman pituuden kuin arkipaiva. Vaihtoehto oli jattaa lauantai
            pois tyopaivista, jolloin katto olisi oikea mutta tarjonta
            20 h/vko alakanttiin — kaksikymmenta on eri suuruusluokka kuin
            nelja, ja siksi tama on valittu.

            Luku on siis vaara ja TIEDETTY, mikä on eri asia kuin vaara.
            Oikea korjaus on paivakohtainen malli (migraatiot 097/098). */}
        {rekisteri && rekisteri.lauantaihenkiloita > 0
          && rekisteri.lauantain_tunnit != null && (
          <p className="selite__raja">
            {t("kap.lauantai_alku", { h: luku(rekisteri.lauantain_tunnit) })}{" "}
            <strong>{rekisteri.lauantaihenkiloita}</strong> {t("kap.lauantai_henkilolla")}
            {" "}{t("kap.lauantai_loppu", {
              a: luku(rekisteri.lauantain_tunnit - 8),
              b: luku((rekisteri.lauantain_tunnit - 8) * rekisteri.lauantaihenkiloita) })}
          </p>
        )}
      </section>

      {/* henkilön aikataulu */}
      <HenkilonAikataulu
        henkilo={henkilo}
        alku={alku}
        loppu={loppu}
        viikot={viikot}
        rivit={rivit}
        sulje={() => setHenkilo(null)}
      />

      {/* allokointi-ikkuna */}
      <AllokointiIkkuna
        valittu={valittu}
        henkilot={henkilot}
        rivi={valittu ? solu(valittu.id, valittu.viikkoAlku) : undefined}
        yksiPaiva={tarkkuus === "paiva"}
        poissaololajit={poissaololajit}
        kohteet={kohteet}
        saaMuokata={saaMuokata}
        sulje={() => setValittu(null)}
        paivita={lataa}
      />

    </>
  );

  function siirra(paivia: number) {
    setAlku(pvmTeksti(lisaaPaivia(new Date(alku), paivia)));
    setValittu(null);
  }
}

/* ═══════════════════════════════════════════════════════════
   Henkilön aikataulu.

   Ruudukko vastaa kysymykseen "kuka on missä". Tämä vastaa
   kysymykseen "missä Jussi on" — rivit ovat kohteita, ei henkilöitä,
   ja sama visuaalinen kieli kuin työmaiden aikajanassa, jotta niitä
   voi lukea peräkkäin.
   ═══════════════════════════════════════════════════════════ */

function HenkilonAikataulu({
  henkilo, alku, loppu, viikot, rivit, sulje,
}: {
  henkilo: KapasiteettiViikko | null;
  alku: string; loppu: string;
  viikot: [string, number][];
  rivit: KapasiteettiViikko[];
  sulje: () => void;
}) {
  const ikkuna = useRef<HTMLDialogElement>(null);
  const [aikataulu, setAikataulu] = useState<HenkilonRivi[]>([]);
  const [poissaolot, setPoissaolot] = useState<Poissaolo[]>([]);
  const [ladataan, setLadataan] = useState(false);

  useEffect(() => {
    const d = ikkuna.current;
    if (!d) return;
    if (henkilo && !d.open) d.showModal();
    if (!henkilo && d.open) d.close();
  }, [henkilo]);

  useEffect(() => {
    if (!henkilo) { setAikataulu([]); setPoissaolot([]); return; }
    setLadataan(true);
    Promise.all([
      api.henkilonAikataulu(henkilo.henkilo_id, alku, loppu),
      api.poissaolot(alku, loppu, henkilo.henkilo_id),
    ])
      .then(([a, p]) => { setAikataulu(a); setPoissaolot(p); })
      .catch(() => { setAikataulu([]); setPoissaolot([]); })
      .finally(() => setLadataan(false));
  }, [henkilo, alku, loppu]);

  /** Kohteet riveiksi, viikot sarakkeiksi. */
  const kohteet = useMemo(() => {
    const m = new Map<number, { tunnus: string; nimi: string; viikot: Map<string, number> }>();
    for (const r of aikataulu) {
      if (!m.has(r.kohde_id)) m.set(r.kohde_id, { tunnus: r.tunnus, nimi: r.nimi, viikot: new Map() });
      const k = m.get(r.kohde_id)!;
      k.viikot.set(r.viikko_alku, (k.viikot.get(r.viikko_alku) ?? 0) + r.tunnit);
    }
    return [...m.entries()].map(([id, k]) => ({ id, ...k }));
  }, [aikataulu]);

  const omatViikot = useMemo(
    () => henkilo ? rivit.filter((r) => r.henkilo_id === henkilo.henkilo_id) : [],
    [rivit, henkilo]
  );

  const yhteensa = aikataulu.reduce((s, r) => s + r.tunnit, 0);

  return (
    <dialog ref={ikkuna} className="ikkuna ikkuna--leveä" onClose={sulje}
            onClick={(e) => { if (e.target === ikkuna.current) sulje(); }}>
      {henkilo && (
        <div className="ikkuna__sisalto">
          <div className="ikkuna__yla">
            <div>
              <strong>{henkilo.nimeamaton ? t("kap.vuokrapaikka") : henkilo.nimi}</strong>
              <span className="mono dim"> · {t("kap.h_ikkunassa", { h: luku(yhteensa) })}</span>
            </div>
            <button className="ikkuna__sulje" onClick={sulje} aria-label={t("hl.sulje")}>×</button>
          </div>

          {ladataan && <p className="dim">{t("ltar.ladataan")}</p>}

          {!ladataan && kohteet.length === 0 && poissaolot.length === 0 && (
            <p className="dim">{t("kap.ei_allokaatioita")}</p>
          )}

          {(kohteet.length > 0 || poissaolot.length > 0) && (
            <div className="scroll">
              <table className="tbl tbl--aikajana">
                <thead>
                  <tr>
                    <th className="th--nimi">{t("kuittaus.kohde")}</th>
                    {viikot.map(([vkAlku, vkNro]) => <th key={vkAlku}>{vkNro}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {kohteet.map((k) => (
                    <tr key={k.id}>
                      <th className="th--nimi">
                        <span className="lyh">{k.tunnus}</span>
                        <span className="kokonimi">{k.nimi}</span>
                      </th>
                      {viikot.map(([vkAlku]) => {
                        const tt = k.viikot.get(vkAlku);
                        return (
                          <td key={vkAlku} className="td--jana">
                            {tt !== undefined && (
                              <span className="jana jana--tunnit" title={`${luku(tt)} h`}>
                                {luku(tt)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                  {poissaolot.length > 0 && (
                    <tr>
                      <th className="th--nimi"><span className="kokonimi">{t("kap.poissa")}</span></th>
                      {viikot.map(([vkAlku]) => {
                        const vkLoppu = pvmTeksti(lisaaPaivia(new Date(vkAlku), 6));
                        const osuu = poissaolot.some(
                          (p) => p.alkaa <= vkLoppu && p.paattyy >= vkAlku
                        );
                        return (
                          <td key={vkAlku} className="td--jana">
                            {osuu && <span className="jana jana--poissa" />}
                          </td>
                        );
                      })}
                    </tr>
                  )}

                  <tr className="tr--summa">
                    <th className="th--nimi">{t("kap.kuormitus")}</th>
                    {viikot.map(([vkAlku]) => {
                      const v = omatViikot.find((r) => r.viikko_alku === vkAlku);
                      const os = v?.kuormitus ?? null;
                      return (
                        <td key={vkAlku}
                            className={"td--summa" + (v?.ylitys ? " td--summa-korkea" : "")}>
                          {os === null || os === 0 ? "" : `${Math.round(os * 100)} %`}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <p className="ikkuna__vihje">
            {t("kap.aikataulu_vihje")}
          </p>
        </div>
      )}
    </dialog>
  );
}

/* ═══════════════════════════════════════════════════════════
   Allokointi-ikkuna.

   Modaali eikä sivun alalaidassa oleva paneeli: 31 riviä ruudukkoa
   tarkoittaa, että paneeliin pitäisi vierittää joka kerta. Natiivi
   <dialog> hoitaa Esc-näppäimen ja kohdistuksen ilman kirjastoa.
   ═══════════════════════════════════════════════════════════ */

function AllokointiIkkuna({
  valittu, rivi, kohteet, henkilot, saaMuokata, yksiPaiva, poissaololajit, sulje, paivita,
}: {
  valittu: { id: number; viikkoAlku: string; alkaa: string; paattyy: string } | null;
  rivi: KapasiteettiViikko | undefined;
  kohteet: Kohde[];
  /** Ruudukon henkilöt (suodatettuina) — joukkoallokoinnin valinta. */
  henkilot: KapasiteettiViikko[];
  saaMuokata: boolean;
  yksiPaiva: boolean;
  poissaololajit: Poissaololaji[];
  sulje: () => void;
  paivita: () => Promise<void>;
}) {
  const ikkuna = useRef<HTMLDialogElement>(null);
  const [allokaatiot, setAllokaatiot] = useState<AllokaatioRivi[]>([]);
  const [kohdeId, setKohdeId] = useState<number | "">("");
  // T589 (arkkitehtuuri vaihe 1, kohdekoukku; Toni 16.10.2026 klo 03.45): oletus yläpalkin kohteesta (useKohde), vaihdettavissa.
  const { valittu: ylaKohde } = useKohde();
  useEffect(() => { if (typeof ylaKohde === "number" && kohdeId === "") setKohdeId(ylaKohde); }, [ylaKohde]);
  const [tunnit, setTunnit] = useState("8");
  const [alkaa, setAlkaa] = useState("");
  const [paattyy, setPaattyy] = useState("");
  const [maksimi, setMaksimi] = useState("");
  const [kaikkiPaivat, setKaikkiPaivat] = useState(false);
  const [poissaolot, setPoissaolot] = useState<Poissaolo[]>([]);
  const [lajiId, setLajiId] = useState<number | "">("");
  const [tallennetaan, setTallennetaan] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  /** T281: muut henkilöt samaan allokointiin. Tyhjä = yksi henkilö, kuten ennen. */
  const [muut, setMuut] = useState<number[]>([]);
  /* T401 rivi 6 (Toni 30.9.2026 «6 kyllä»): este ja peruste NAPIN VIERESSÄ, ei alalaidassa.
     `esteet` haetaan samalla säännöllä kuin tallennuksen portti (GET /api/allokaatio/esteet) heti
     kun kohde, jakso tai henkilövalinta muuttuu — käyttäjä näkee ennen painallusta, keneltä
     pakollinen pätevyys puuttuu, ja kenttä perusteelle on siinä. Mitattu 30.9.: viisi 400-yritystä
     protot'ssa, koska viesti piirtyi Työaika-asetusten alle eikä perustekenttää ollut lainkaan. */
  const [esteet, setEsteet] = useState<{ resurssi_id: number; nimi: string; puuttuu: string }[]>([]);
  const [peruste, setPeruste] = useState("");
  const [perusteVaaditaan, setPerusteVaaditaan] = useState(false);
  /* T401 rivi 7 (Toni 30.9.2026 «7kyllä. Vaatimus on AINA tehtäväresurssikohtainen.»): allokointi kysyy
     tehtävän. Kohteen jakson tarpeet tulevat esteet-kutsusta; yksi → valittuna ja näkyvissä, useampi →
     valinta ennen henkilövalintaa, eikä tallennus lähde ilman sitä. Esteet luetaan vain valitusta tarpeesta. */
  const [tarpeet, setTarpeet] = useState<{ id: number; osaamisluokka: string; alkaa: string; paattyy: string; maara: number; vaatimuksia: number }[]>([]);
  const [tarveId, setTarveId] = useState<number | "">("");
  const [muutHaku, setMuutHaku] = useState("");

  const alku = valittu?.viikkoAlku ?? "";

  /**
   * Listaus- ja poistoväli.
   *
   * Päivätilassa yksi päivä: silloin "Poista" tarkoittaa vuoron
   * poistoa, ei koko viikon. Viikkotilassa maanantaista sunnuntaihin
   * — ei perjantaihin, muuten viikonlopputyö jää listalta pois ja
   * näyttää siltä ettei sitä ole.
   */
  const listausAlku  = valittu ? (yksiPaiva ? valittu.alkaa : alku) : "";
  const listausLoppu = valittu
    ? (yksiPaiva ? valittu.alkaa : pvmTeksti(lisaaPaivia(new Date(alku), 6)))
    : "";

  useEffect(() => {
    const d = ikkuna.current;
    if (!d) return;
    if (valittu && !d.open) d.showModal();
    if (!valittu && d.open) d.close();
  }, [valittu]);

  // Lomakkeen nollaus vain kun valittu ruutu vaihtuu. 9.10.2026: nollaus oli samassa effectissä kuin `rivi`, ja tallennuksen
  // jälkeinen paivitaKaikki() antaa rivistä uuden olion — «Tallennettu …» pyyhkiytyi heti näkyvistä (kävely: POST 200, rivit
  // kannassa, ilmoitusta ei 15 s:ssa; näkyi vasta kun palvelin nopeutui 2 vCPU:hun ja päivitys ehti ennen kyselyä).
  useEffect(() => {
    setIlmoitus(null);
    setMuut([]);
    setMuutHaku("");
    setPeruste(""); setPerusteVaaditaan(false); setEsteet([]); setTarpeet([]); setTarveId("");
    // Esitäyttö klikatusta ruudusta: viikkotilassa ma–pe, päivätilassa
    // yksi päivä. Molemmat ovat silti vapaasti muutettavissa —
    // seisokki voi alkaa torstaina ja loppua tiistaina.
    setAlkaa(valittu?.alkaa ?? "");
    setPaattyy(valittu?.paattyy ?? "");
  }, [valittu]);

  useEffect(() => {
    if (!valittu) { setAllokaatiot([]); return; }
    if (rivi) setMaksimi(String(rivi.maksimi / Math.max(rivi.tyopaivia, 1)));
    // Päätös 99: lista on poistonapin lähde — edellisen henkilön myöhästynyt vastaus ei saa täyttää sitä.
    let elossa = true;
    setAllokaatiot([]);
    api.allokaatiot(valittu.id, listausAlku, listausLoppu)
      .then((a) => { if (elossa) setAllokaatiot(a); })
      .catch(() => { if (elossa) setAllokaatiot([]); });
    api.poissaolot(listausAlku, listausLoppu, valittu.id)
      .then((a) => { if (elossa) setPoissaolot(a); })
      .catch(() => { if (elossa) setPoissaolot([]); });
    return () => { elossa = false; };
  }, [valittu, listausAlku, listausLoppu, rivi]);

  async function paivitaKaikki() {
    if (!valittu) return;
    await Promise.all([
      paivita(),
      api.allokaatiot(valittu.id, listausAlku, listausLoppu).then(setAllokaatiot),
      api.poissaolot(listausAlku, listausLoppu, valittu.id).then(setPoissaolot),
    ]);
  }

  async function tallenna() {
    if (!valittu || kohdeId === "") return;
    const maara = Number(tunnit.replace(",", "."));
    if (!(maara > 0 && maara <= 24)) {
      setIlmoitus(t("kap.tunnit_0_24"));
      return;
    }
    if (!alkaa || !paattyy) { setIlmoitus(t("kap.anna_paivat")); return; }
    if (paattyy < alkaa) { setIlmoitus(t("kap.loppu_ennen_alkua")); return; }

    setTallennetaan(true);
    setIlmoitus(null);
    try {
      const joukko = muut.length > 0;
      const tulos = await api.allokoi({
        ...(joukko ? { resurssi_idt: [valittu.id, ...muut] } : { resurssi_id: valittu.id }),
        kohde_id: kohdeId,
        alkaa, paattyy, tunnit_per_paiva: maara,
        paivat: kaikkiPaivat ? "kaikki" : "tyopaivat",
        ...(peruste.trim() ? { peruste: peruste.trim() } : {}),
        ...(tarveId !== "" ? { tarve_id: tarveId } : {}),
      });
      setPeruste(""); setPerusteVaaditaan(false);
      const ylitys = tulos.varoitukset.some((v) => v.ylitys);
      const konflikti = tulos.varoitukset.some((v) => v.virhe_poissa);
      if (joukko) {
        // Henkilöittäin: nolla päivää, poissaolo ja ylitys nimetään eikä niitä summata piiloon.
        const nimi = (id: number) => henkilot.find((h) => h.henkilo_id === id)?.nimi ?? `#${id}`;
        const nollat = tulos.henkilot.filter((h) => h.paivia === 0).map((h) => nimi(h.resurssi_id));
        const poissa = new Set(tulos.varoitukset.filter((v) => v.virhe_poissa).map((v) => Number(v.henkilo_id)));
        const ylittyy = new Set(tulos.varoitukset.filter((v) => v.ylitys).map((v) => Number(v.henkilo_id)));
        setIlmoitus(
          t("kap.tallennettu_joukko", { n: tulos.henkilot.length, p: tulos.paivia })
          + (nollat.length ? " " + t("kap.joukko_nollat", { nimet: nollat.join(", ") }) : "")
          + (poissa.size ? " " + t("kap.joukko_poissa", { nimet: [...poissa].map(nimi).join(", ") }) : "")
          + (ylittyy.size ? " " + t("kap.joukko_ylittyy", { nimet: [...ylittyy].map(nimi).join(", ") }) : ""),
        );
        setMuut([]);
      } else setIlmoitus(
        tulos.paivia === 0
          ? t("kap.ei_tyopaivia")
        : konflikti
          ? t("kap.tallennettu_poissaolo", { p: tulos.paivia })
        : ylitys
          ? t("kap.tallennettu_ylitys", { p: tulos.paivia })
          : t("kap.tallennettu", { p: tulos.paivia })
      );
      await paivitaKaikki();
    } catch (e) {
      // 145 / T401: portti ei ole virhe vaan kysymys — kenttä avautuu napin kohdalla.
      if (e instanceof ApiError && e.virhe === "peruste_puuttuu") setPerusteVaaditaan(true);
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kap.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  const valittuKohde = kohdeId === "" ? undefined : kohteet.find((k) => k.id === kohdeId);

  /* Esteet ennen painallusta. Tyhjä kohde tai jakso → ei kysytä, ei estettä. Vanhentunut vastaus
     hylätään (kohde ehti vaihtua) — muuten väärän kohteen este jäisi ruudulle. */
  useEffect(() => {
    if (!valittu || kohdeId === "" || !alkaa || !paattyy || paattyy < alkaa) { setEsteet([]); setTarpeet([]); return; }
    let voimassa = true;
    api.allokaatioEsteet({ kohde_id: kohdeId, alkaa, paattyy, resurssi_idt: [valittu.id, ...muut],
                           ...(tarveId !== "" ? { tarve_id: tarveId } : {}) })
      .then((v) => {
        if (!voimassa) return;
        setEsteet(v.puuttuvat); setTarpeet(v.tarpeet);
        // Yksi tarve valitaan itse ja näytetään; valittu tarve joka ei enää osu jaksolle nollataan.
        if (v.tarve_valinta === "yksi" && v.tarve_id != null && tarveId !== v.tarve_id) setTarveId(v.tarve_id);
        if (tarveId !== "" && !v.tarpeet.some((t) => t.id === tarveId)) setTarveId("");
      })
      .catch(() => { if (voimassa) { setEsteet([]); setTarpeet([]); } });
    return () => { voimassa = false; };
  }, [valittu, kohdeId, alkaa, paattyy, muut, tarveId]);

  /**
   * Osuuko allokaatio kohteen ulkopuolelle.
   *
   * Ei estä tallennusta — työtä voi tehdä ennen virallista aloitusta
   * ja jälkitöitä sen jälkeen. Mutta viikolle 31 allokoiminen
   * projektiin, joka alkaa 15.8., on lähes varmasti erehdys, eikä
   * mikään muu kerro siitä.
   */
  const jaksoUlkona = (() => {
    if (!valittuKohde || !alkaa || !paattyy) return null;
    const ka = valittuKohde.alkaa, kp = valittuKohde.paattyy;
    if (ka && paattyy < ka) return t("kap.jakso_ennen", { pvm: ka });
    if (kp && alkaa > kp) return t("kap.jakso_jalkeen", { pvm: kp });
    if (ka && alkaa < ka) return t("kap.alkaa_ennen", { pvm: ka });
    if (kp && paattyy > kp) return t("kap.jatkuu_jalkeen", { pvm: kp });
    return null;
  })();

  /** Pikavalinta: n kalenteriviikkoa alkupäivästä. */
  function asetaJakso(viikkoja: number) {
    if (!alkaa) return;
    setPaattyy(pvmTeksti(lisaaPaivia(new Date(alkaa), viikkoja * 7 - 1)));
  }

  async function poista(kohde_id: number) {
    if (!valittu) return;
    setTallennetaan(true);
    try {
      await api.poistaAllokaatio({
        resurssi_id: valittu.id, kohde_id,
        alkaa: listausAlku, paattyy: listausLoppu,
      });
      setIlmoitus(t("kap.poistettu"));
      await paivitaKaikki();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kap.poisto_virhe"));
    } finally { setTallennetaan(false); }
  }

  async function merkitsePoissaolo() {
    if (!valittu || lajiId === "") return;
    if (!alkaa || !paattyy) { setIlmoitus(t("kap.anna_paivat")); return; }
    if (paattyy < alkaa) { setIlmoitus(t("kap.loppu_ennen_alkua")); return; }
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.merkitsePoissaolo({
        henkilo_id: valittu.id, poissaololaji_id: lajiId, alkaa, paattyy,
      });
      setIlmoitus(t("kap.poissaolo_merkitty"));
      await paivitaKaikki();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kap.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  async function poistaPoissaolo(id: number) {
    setTallennetaan(true);
    try {
      await api.poistaPoissaolo(id);
      setIlmoitus(t("kap.poissaolo_poistettu"));
      await paivitaKaikki();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kap.poisto_virhe"));
    } finally { setTallennetaan(false); }
  }

  async function tallennaMaksimi() {
    if (!valittu) return;
    const m = Number(maksimi.replace(",", "."));
    if (!(m > 0 && m <= 24)) { setIlmoitus(t("kap.maksimi_0_24")); return; }
    setTallennetaan(true);
    try {
      await api.muokkaaHenkiloa(valittu.id, { maksimi_per_paiva: m });
      setIlmoitus(t("kap.maksimi_tallennettu", { h: luku(m) }));
      await paivitaKaikki();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kap.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <dialog ref={ikkuna} className="ikkuna" onClose={sulje}
            onClick={(e) => { if (e.target === ikkuna.current) sulje(); }}>
      {valittu && rivi && (
        <div className="ikkuna__sisalto">
          <div className="ikkuna__yla">
            <div>
              <strong>{rivi.nimeamaton ? t("kap.vuokrapaikka") : rivi.nimi}</strong>
              <span className="mono dim">
                {" · "}
                {yksiPaiva && valittu.alkaa === valittu.paattyy
                  ? paivanNimi(valittu.alkaa, true)
                  : t("kap.viikko", { n: rivi.viikko })}
              </span>
            </div>
            <button className="ikkuna__sulje" onClick={sulje} aria-label={t("hl.sulje")}>×</button>
          </div>

          <dl className="luvut">
            <div><dt>{t("kap.allokoitu_vk", { n: rivi.viikko })}</dt><dd>{luku(rivi.allokoitu)} h</dd></div>
            <div><dt>{t("kap.kaytettavissa")}</dt><dd>{luku(rivi.kaytettavissa)} h</dd></div>
            <div><dt>{t("kap.maksimi")}</dt><dd>{luku(rivi.maksimi)} h</dd></div>
            <div><dt>{t("kap.vapaana")}</dt><dd>{luku(rivi.vapaa)} h</dd></div>
          </dl>
          {/* Päätös 1.10.2026 nro 4: nimellinen viikko (työaikamalli) voi ylittää henkilön viikkokaton.
              Vapaa lasketaan katosta (003), ja ero sanotaan — hiljainen pienempi luku ei kerro miksi. */}
          {rivi.kaytettavissa > rivi.maksimi && (
            <p className="huom" data-viikkokatto-ylittyy="">
              <Tilasana laji="varoitus">{t("kap.nimellinen_ylittaa", { a: luku(rivi.kaytettavissa), b: luku(rivi.maksimi) })}</Tilasana>
              {" "}— {t("kap.vapaa_katosta")}
            </p>
          )}

          {rivi.poissa_paivia > 0 && <p className="huom">{t("kap.poissa_tyopaivaa", { n: rivi.poissa_paivia })}</p>}
      {rivi.poikkeuspaivia > 0 && (
        <p className="huom">
          {t("kap.poikkeuspaivaa", { n: rivi.poikkeuspaivia })}
        </p>
      )}
          {rivi.virhe_poissa && <p className="huom huom--vika">{t("kap.allokoitu_poissaolon_paalle")}</p>}
          {rivi.ylitys && <p className="huom huom--vika">{t("kap.ylittaa_maksimin", { h: luku(rivi.allokoitu - rivi.maksimi) })}</p>}

          {allokaatiot.length > 0 && (
            <ul className="kohdelista">
              {allokaatiot.map((a) => (
                /* Kohde joka ei varaa kapasiteettia: rivi jaa nakyviin
                   mutta merkitaan. Ilman merkintaa tyonjohtaja nakisi
                   merkinnan ja vapaan ihmisen eika ymmartaisi kumpi on
                   totta - ja piilottaminen olisi viela huonompi, koska
                   silloin suunnitelma katoaisi nakyvista kokonaan. */
                <li key={`${a.kohde_id}-${a.laji}`}
                    className={varaaKapasiteettia(a.kohde_tila) ? "" : "kohderivi--ei-varaa"}>
                  <span>
                    <span className="mono">{a.tunnus}</span> {a.nimi}
                    {!varaaKapasiteettia(a.kohde_tila) && (
                      <span className="merkki merkki--ei-varaa"
                            title={selite("kohde_tila", a.kohde_tila)}>
                        {nayttonimi("kohde_tila", a.kohde_tila)} · {t("kap.ei_kuormita")}
                      </span>
                    )}
                  </span>
                  <span className="mono">{luku(a.tunnit_per_paiva)} h/pv · {a.paivia} pv</span>
                  {saaMuokata && (
                    <button className="nappi nappi--kevyt" disabled={tallennetaan}
                            onClick={() => void poista(a.kohde_id)}>
                      {yksiPaiva ? t("kap.poista_vuoro") : t("kap.poista_viikolta")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {poissaolot.length > 0 && (
            <ul className="kohdelista kohdelista--poissa">
              {poissaolot.map((p) => (
                <li key={p.id}>
                  <span>{p.laji}</span>
                  <span className="mono">{naytaPvm(p.alkaa)} – {naytaPvm(p.paattyy)}</span>
                  {saaMuokata && (
                    <button className="nappi nappi--kevyt" disabled={tallennetaan}
                            onClick={() => void poistaPoissaolo(p.id)}>{t("liite.poista")}</button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {saaMuokata && (
            <>
              <div className="allokointi">
                <label className="kasvaa">
                  {t("kuittaus.kohde")}
                  <select value={kohdeId} data-kohdekoukku="kapasiteetti"
                          onChange={(e) => { setKohdeId(e.target.value ? Number(e.target.value) : ""); setTarveId(""); }}>
                    <option value="">{t("kap.valitse_kohde")}</option>
                    {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
                  </select>
                </label>
                {/* T401 rivi 7: mihin tehtävään. Yksi tarve → näkyy valittuna; useampi → kysytään ENNEN henkilövalintaa;
                    ei tarpeita → allokaatio kohteelle ilman tehtävää (eikä vaatimuksia, koska kohteella ei ole niitä). */}
                {kohdeId !== "" && tarpeet.length > 0 && (
                  <label className="kasvaa" data-allokointi-tarve={tarpeet.length === 1 ? "yksi" : "monta"}>
                    {t("kap.tehtava_tarve")}
                    <select value={tarveId} onChange={(e) => setTarveId(e.target.value ? Number(e.target.value) : "")}>
                      {tarpeet.length > 1 && <option value="">{t("kap.valitse_tehtava", { n: tarpeet.length })}</option>}
                      {tarpeet.map((tp) => (
                        <option key={tp.id} value={tp.id}>
                          {tp.osaamisluokka} · {naytaPvm(tp.alkaa)} – {naytaPvm(tp.paattyy)} · {t("kap.hlo", { n: tp.maara })}
                          {tp.vaatimuksia > 0 ? ` · ${tp.vaatimuksia > 1 ? t("kap.vaatimusta", { n: tp.vaatimuksia }) : t("kap.vaatimus", { n: tp.vaatimuksia })}` : ` · ${t("kap.ei_vaatimuksia")}`}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  {t("kap.tuntia_pv")}
                  <input type="text" inputMode="decimal" size={4} value={tunnit}
                         onChange={(e) => setTunnit(e.target.value)} />
                </label>
              </div>

              <div className="allokointi allokointi--jatko">
                <label>
                  {t("rot.alkaa")}
                  <input type="date" value={alkaa} onChange={(e) => setAlkaa(e.target.value)} />
                </label>
                <label>
                  {t("rot.paattyy")}
                  <input type="date" value={paattyy} onChange={(e) => setPaattyy(e.target.value)} />
                </label>
              </div>

              {/* T401 rivi 6: este ja peruste ennen painallusta; 400 peruste_puuttuu avaa kentän tähän eikä alalaitaan.
                  Päätös 1.10.2026 nro 5 (Toni): laatikko Allokoi-napin YLÄPUOLELLE — puute → peruste → nappi. */}
              {(esteet.length > 0 || perusteVaaditaan) && (
                <Estelaatikko esteAttr="data-allokointi-este" perusteAttr="data-allokointi-peruste"
                              selite={t("kap.este_selite")}
                              rivit={esteet.length > 0
                                ? esteet.map((e) => ({ avain: e.resurssi_id, nimi: e.nimi, puuttuu: e.puuttuu }))
                                : [{ avain: "ilmoitus", nimi: t("kap.palvelin"), puuttuu: t("kap.katso_viesti") }]}
                              peruste={peruste} setPeruste={setPeruste}
                              placeholder={t("kap.peruste_esim")} />
              )}
              <div className="allokointi allokointi--jatko" data-allokointi-nappi="">
                <button className="nappi nappi--paa" disabled={tallennetaan || kohdeId === "" || (tarpeet.length > 1 && tarveId === "")}
                        title={tarpeet.length > 1 && tarveId === "" ? t("kap.valitse_ensin_tehtava") : undefined}
                        onClick={() => void tallenna()}>
                  {tallennetaan ? t("kap.tallennetaan") : muut.length > 0 ? t("kap.allokoi_n", { n: muut.length + 1 }) : t("kap.allokoi")}
                </button>
                {ilmoitus && <p className="huom" role="status" data-allokointi-ilmoitus="" style={{ margin: 0 }}>{ilmoitus}</p>}
              </div>

              {/* T281 (13.9.2026): joukkoallokointi. Sama kohde, jakso ja tunnit kaikille, kukin omalla
                  työpäiväkuviollaan. Kenttätilin näkyvyys kohteeseen seuraa allokaatiosta. */}
              <details data-joukkoallokointi="">
                <summary>{t("kap.allokoi_muita")}{muut.length > 0 ? ` ${t("kap.valittu_lkm", { n: muut.length })}` : ""}</summary>
                <input value={muutHaku} placeholder={t("kap.hae_nimella")} autoComplete="off"
                       onChange={(e) => setMuutHaku(e.target.value)} style={{ maxWidth: 260, marginTop: 6 }} />
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", padding: "6px 0",
                              maxHeight: 180, overflowY: "auto" }}>
                  {henkilot.filter((h) => h.henkilo_id !== valittu.id)
                    .filter((h) => !muutHaku.trim()
                      || `${h.nimi} ${h.lyhenne ?? ""}`.toLowerCase().includes(muutHaku.trim().toLowerCase()))
                    .map((h) => (
                    <label key={h.henkilo_id} className="valinta">
                      <input type="checkbox" checked={muut.includes(h.henkilo_id)}
                             onChange={(e) => setMuut(e.target.checked
                               ? [...muut, h.henkilo_id]
                               : muut.filter((x) => x !== h.henkilo_id))} />
                      {h.nimeamaton ? t("kap.vuokrapaikka") : h.nimi}
                    </label>
                  ))}
                </div>
                <p className="hint">
                  {t("kap.joukko_vihje")}
                </p>
              </details>

              {valittuKohde && (
                <p className={"kohdetieto" + (jaksoUlkona ? " kohdetieto--huomio" : "")}>
                  <span className="mono">{valittuKohde.tunnus}</span>{" "}
                  {valittuKohde.laji === "jatkuva"
                    ? t("kap.jatkuva_sopimus", { pvm: naytaPvm(valittuKohde.alkaa) ?? "—" })
                    : t("kap.kaynnissa", { a: naytaPvm(valittuKohde.alkaa) ?? "—", b: naytaPvm(valittuKohde.paattyy) ?? "—" })}
                  {jaksoUlkona && <> · <strong>{jaksoUlkona}</strong></>}
                </p>
              )}

              <label className="valinta valinta--rivi">
                <input type="checkbox" checked={kaikkiPaivat}
                       onChange={(e) => setKaikkiPaivat(e.target.checked)} />
                {t("kap.myos_viikonloput")}
              </label>

              <div className="pikavalinnat">
                <span className="pikavalinnat__otsikko">{t("kap.pikavalinta")}</span>
                {[1, 2, 4, 6].map((v) => (
                  <button key={v} className="nappi nappi--kevyt" type="button"
                          onClick={() => asetaJakso(v)}>{t("kap.vko", { n: v })}</button>
                ))}
                <button className="nappi nappi--kevyt" type="button"
                        onClick={() => { setAlkaa(alku); setPaattyy(perjantai(alku)); }}>
                  {t("kap.tama_viikko")}
                </button>
                {valittuKohde?.alkaa && (
                  <button className="nappi nappi--kevyt" type="button"
                          onClick={() => {
                            setAlkaa(valittuKohde.alkaa!);
                            if (valittuKohde.paattyy) setPaattyy(valittuKohde.paattyy);
                          }}>
                    {t("kap.koko_kohde")}
                  </button>
                )}
              </div>

              <p className="ikkuna__vihje">
                {t("kap.jakso_vihje")}
              </p>

              {!rivi.nimeamaton && (
                <details className="tyoaika">
                  <summary>{t("kap.merkitse_poissaolo")}</summary>
                  <div className="allokointi">
                    <label className="kasvaa">
                      {t("kap.laji")}
                      <select value={lajiId}
                              onChange={(e) => setLajiId(e.target.value ? Number(e.target.value) : "")}>
                        <option value="">{t("kap.valitse")}</option>
                        {poissaololajit.map((l) => (
                          <option key={l.id} value={l.id}>{l.nimi}</option>
                        ))}
                      </select>
                    </label>
                    <button className="nappi nappi--kevyt" disabled={tallennetaan || lajiId === ""}
                            onClick={() => void merkitsePoissaolo()}>
                      {t("kap.merkitse_poissaolevaksi")}
                    </button>
                  </div>
                  <p className="ikkuna__vihje">
                    {t("kap.poissaolo_vihje")}
                  </p>
                </details>
              )}

              <details className="tyoaika">
                <summary>{t("kap.tyoaika_asetukset")}</summary>
                <div className="allokointi">
                  <label>
                    {t("kap.maksimi_hpv")}
                    <input type="text" inputMode="decimal" size={4} value={maksimi}
                           onChange={(e) => setMaksimi(e.target.value)} />
                  </label>
                  <button className="nappi nappi--kevyt" disabled={tallennetaan}
                          onClick={() => void tallennaMaksimi()}>{t("kap.tallenna_maksimi")}</button>
                </div>
                <p className="ikkuna__vihje">
                  {t("kap.maksimi_vihje")}
                </p>
              </details>
            </>
          )}

        </div>
      )}
    </dialog>
  );
}

/* ── apurit ─────────────────────────────────────────────── */



/**
 * Lauantai ja sunnuntai eivät ole sama asia. Sunnuntaityö on
 * työaikalain mukaan sadan prosentin korotus, lauantai ei — ja
 * sunnuntai on se päivä, jota ei vahingossa haluta allokoida.
 */
function viikonloppuLuokka(pvm: string): string {
  const d = new Date(pvm).getUTCDay();
  if (d === 0) return " on-sunnuntai";
  if (d === 6) return " on-lauantai";
  return "";
}

/** "2026-08-13" → "to 13.8." tai pitkänä "torstai 13.8.2026" */
function paivanNimi(pvm: string, pitka = false): string {
  const d = new Date(pvm);
  return pitka
    ? d.toLocaleDateString(lokaali(), { weekday: "long", day: "numeric", month: "numeric", year: "numeric" })
    : `${d.toLocaleDateString(lokaali(), { weekday: "short" })} ${d.getDate()}.${d.getMonth() + 1}.`;
}

/** Päiväsolun tila. Päivätasolla maksimi on päivämaksimi. */
function paivaLuokka(p: KapasiteettiPaiva | undefined, vuokra: boolean) {
  if (!p) return "solu";
  const tunnit = p.vahvistettu + p.ehdotus;
  if (p.virhe_poissa) return "solu solu--ristiriita";
  // Viikonloppu- tai pyhätyö: tehtyä työtä, ei virhe. Erottuu omalla
  // merkinnällään eikä sekoitu poissaolon päälle allokointiin.
  if (p.poikkeuspaiva) return "solu solu--poikkeus";
  if (p.kaytettavissa === 0) return "solu solu--poissa";
  if (p.ylitys) return "solu solu--yli";
  if (tunnit === 0) return vuokra ? "solu solu--vuokravapaa" : "solu solu--vapaa";
  const os = p.maksimi > 0 ? tunnit / p.maksimi : 0;
  if (os >= 0.95) return "solu solu--taysi";
  if (os >= 0.5) return "solu solu--osa";
  return "solu solu--kevyt";
}

/** Viikon alkua seuraava maanantai, "YYYY-MM-DD". */
function viikonLoppu(alku: string): string {
  const d = new Date(alku + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 7);
  return d.toISOString().slice(0, 10);
}

/** "2026-08-01" → "elokuu 2026" */
const kuukaudenNimi = (pvm: string) =>
  new Date(pvm).toLocaleDateString(lokaali(), { month: "long", year: "numeric" });

function soluLuokka(r: KapasiteettiViikko | undefined, vuokra: boolean) {
  if (!r) return "solu";
  if (r.virhe_poissa) return "solu solu--ristiriita";
  if (r.kaytettavissa === 0) return "solu solu--poissa";
  if (r.ylitys) return "solu solu--yli";
  if (r.allokoitu === 0) return vuokra ? "solu solu--vuokravapaa" : "solu solu--vapaa";
  const os = r.kuormitus ?? 0;
  if (os >= 0.95) return "solu solu--taysi";
  if (os >= 0.5) return "solu solu--osa";
  return "solu solu--kevyt";
}
